/**
 * Экран «В обращении найдено N самостоятельных обращений» (ТЗ §6.2, ответ мини
 * «найдено несколько»). Пользователь объединяет соседние фрагменты и разделяет их
 * обратно, результат уходит массивом `groups` в `POST /api/inbox/:id/confirm-segments`.
 *
 * Пустые группы и группы короче 10 знаков n8n отбрасывает молча
 * (`docs/spec/_api-contract.md`, раздел 5), поэтому такие группы помечены явно
 * и посчитаны в заголовке — иначе пользователь не узнает о потере фрагмента.
 *
 * Отправку ведёт родитель (IngestProgress): компонент только собирает `groups`.
 */

import { useEffect, useState } from 'react';
import type { InboxSegment } from '@/api/types';
import { FormError } from '@/components/ErrorState';
import { appealWord, plural } from '@/lib/format';

/** Минимальная длина группы, при которой n8n её принимает. */
export const MIN_GROUP_LENGTH = 10;

export interface SegmentsConfirmProps {
    segments: InboxSegment[];
    pending?: boolean;
    error?: string | null;
    onConfirm: (groups: number[][]) => void;
    onCancel?: () => void;
}

/** Одна группа = один будущий текст обращения: превью фрагментов склеиваются. */
function groupText(group: number[], byIndex: Map<number, InboxSegment>): string {
    return group
        .map((index) => byIndex.get(index)?.preview ?? '')
        .join(' ')
        .trim();
}

export default function SegmentsConfirm({
    segments,
    pending,
    error,
    onConfirm,
    onCancel,
}: SegmentsConfirmProps) {
    const segmentsKey = segments.map((segment) => segment.index).join(',');
    const [groups, setGroups] = useState<number[][]>(() => segments.map((segment) => [segment.index]));

    // Новый набор сегментов (другое входящее, повторный разбор) — начинаем с фрагмента на дело.
    useEffect(() => {
        setGroups(segments.map((segment) => [segment.index]));
        // segmentsKey меняется только вместе с набором сегментов.
    }, [segmentsKey]);

    const byIndex = new Map(segments.map((segment) => [segment.index, segment] as const));

    const mergeWithNext = (position: number) => {
        setGroups((prev) => {
            if (position >= prev.length - 1) return prev;
            const next = prev.slice();
            next.splice(position, 2, [...prev[position], ...prev[position + 1]]);
            return next;
        });
    };

    const splitGroup = (position: number) => {
        setGroups((prev) => {
            const target = prev[position];
            if (!target || target.length < 2) return prev;
            const next = prev.slice();
            next.splice(position, 1, ...target.map((index) => [index]));
            return next;
        });
    };

    const reset = () => setGroups(segments.map((segment) => [segment.index]));

    const kept = groups.filter((group) => groupText(group, byIndex).length >= MIN_GROUP_LENGTH).length;
    const dropped = groups.length - kept;

    const submit = () => {
        if (pending || kept === 0) return;
        onConfirm(groups.filter((group) => group.length > 0));
    };

    return (
        <div>
            <div className="section-title-row">
                <h2>
                    В обращении найдено {segments.length} самостоятельных {appealWord(segments.length)}
                </h2>
                <span className="pill">
                    будет заведено дел: {kept}
                    {dropped > 0 ? ` · отброшено: ${dropped}` : ''}
                </span>
            </div>

            <div className="modal-note">
                ИИ разделил текст на самостоятельные обращения — по каждому будет заведено отдельное дело.
                Если фрагменты на самом деле про одно и то же, объедините их: объединяются только соседние.
                Группа короче {MIN_GROUP_LENGTH} знаков отбрасывается при разборе — присоедините такой фрагмент
                к соседнему, иначе он потеряется.
            </div>

            {groups.map((group, position) => {
                const text = groupText(group, byIndex);
                const tooShort = text.length < MIN_GROUP_LENGTH;
                return (
                    <div className="detail-box" key={`g-${group.join('-')}`}>
                        <div className="detail-title">
                            <span>
                                Обращение {position + 1}
                                {group.length > 1
                                    ? ` · объединено ${group.length} ${plural(group.length, 'фрагмент', 'фрагмента', 'фрагментов')}`
                                    : ''}
                            </span>
                            <span className="period-note">{text.length} знаков в превью</span>
                        </div>

                        {group.map((index) => {
                            const segment = byIndex.get(index);
                            if (!segment) return null;
                            return (
                                <div className="check-item" key={index}>
                                    <span className="check-src">фрагмент {index + 1}</span>
                                    <span className="check-text">
                                        {segment.title || 'Без заголовка'}
                                        <br />
                                        <span className="my-appl">{segment.preview}</span>
                                    </span>
                                </div>
                            );
                        })}

                        {tooShort ? (
                            <FormError>
                                Меньше {MIN_GROUP_LENGTH} знаков — при разборе эта группа будет отброшена.
                                Объедините её с соседней.
                            </FormError>
                        ) : null}

                        <div className="draft-bar">
                            {position < groups.length - 1 ? (
                                <button
                                    type="button"
                                    className="act-btn"
                                    onClick={() => mergeWithNext(position)}
                                >
                                    ↓ Объединить со следующим
                                </button>
                            ) : null}
                            {group.length > 1 ? (
                                <button type="button" className="act-btn" onClick={() => splitGroup(position)}>
                                    ↕ Разделить обратно
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            })}

            <FormError>{error}</FormError>

            <div className="draft-bar">
                <button type="button" className="act-btn primary" disabled={pending || kept === 0} onClick={submit}>
                    {pending ? 'Отправляем…' : 'Отправить в разбор'}
                </button>
                <button type="button" className="act-btn" disabled={pending} onClick={reset}>
                    Вернуть исходное разбиение
                </button>
                {onCancel ? (
                    <button type="button" className="act-btn" disabled={pending} onClick={onCancel}>
                        Отмена
                    </button>
                ) : null}
                <span className="draft-note">
                    {kept === 0
                        ? 'Ни одна группа не проходит по длине — объедините фрагменты.'
                        : `Будет заведено дел: ${kept}. Разбор каждого занимает обычно 5–10 минут.`}
                </span>
            </div>
        </div>
    );
}
